// src/scene/stages/IntroStage.tsx
/**
 * Intro Stage
 *
 * Welcome panel with voice interaction and contact links.
 * First stage visitors see - makes strong first impression.
 */

import { useState, useRef, memo } from 'react'
import { useFrame } from '@react-three/fiber'
import { Container, Text, Svg } from '@react-three/uikit'
import { Button } from '@react-three/uikit-default'
import { useControls, folder } from 'leva'
import { StageShell } from '../StageShell'
import { useResponsiveLayout, useDesktopLayout } from '../useResponsiveLayout'
import {
  ui,
  Panel,
  VStack,
  HStack,
  Body,
  Caption,
  Badge,
  PanelHeader,
  Section,
  Card,
  useHoverScale,
} from '../ui'
import { useVoiceState } from '../../hooks/useVoice'

// SVG icon content for uikit Svg component
const ICONS = {
  github: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>`,
  linkedin: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z"/><path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z"/></svg>`,
}

// Pre-compute data URIs once (avoids recreating strings on every render)
const ICON_SRCS = {
  github: `data:image/svg+xml,${encodeURIComponent(ICONS.github)}`,
  linkedin: `data:image/svg+xml,${encodeURIComponent(ICONS.linkedin)}`,
  mail: `data:image/svg+xml,${encodeURIComponent(ICONS.mail)}`,
} as const

const SOCIAL_LINKS = [
  { icon: 'github' as const, href: 'https://github.com/sepehrnour', label: 'GitHub' },
  { icon: 'linkedin' as const, href: 'https://linkedin.com/in/sepehr-nour', label: 'LinkedIn' },
  { icon: 'mail' as const, href: 'mailto:sepehrnour@gmail.com', label: 'Email' },
]

const EDUCATION = {
  degree: 'B.S. Computer Science',
  school: 'California State University, Fullerton',
  year: '2025',
  focus: 'Machine Learning & Artificial Intelligence',
}

// const EDUCATION = {
//   degree: 'B.S. Computer Science',
//   school: 'Local Group Home',
//   year: '2025',
//   focus: 'Machine Learning & Artificial Intelligence',
// }

// ============================================================================
// INTRO STAGE
// ============================================================================

// Stage 1 (Intro) UI defaults
const STAGE1_MOBILE_POSITION: [number, number, number] = [0, 0.22, 2]
const STAGE1_TABLET_POSITION: [number, number, number] = [0, 0.22, 2]  // Adjust for tablet
const STAGE1_UI_ROTATION: [number, number, number] = [-0.15, 0, 0]

export function IntroStage() {
  const { isTablet } = useResponsiveLayout()
  const { isDesktopLayout } = useDesktopLayout()

  // Select position based on breakpoint
  const defaultPosition = isTablet ? STAGE1_TABLET_POSITION : STAGE1_MOBILE_POSITION

  // Leva controls for Stage 1 UI position and rotation (mobile/tablet mode)
  const {
    uiPosX, uiPosY, uiPosZ,
    uiRotX, uiRotY, uiRotZ,
  } = useControls('Stage 1 UI', {
    Position: folder({
      uiPosX: { value: defaultPosition[0], min: -5, max: 5, step: 0.01 },
      uiPosY: { value: defaultPosition[1], min: -5, max: 5, step: 0.01 },
      uiPosZ: { value: defaultPosition[2], min: -5, max: 5, step: 0.01 },
    }),
    Rotation: folder({
      uiRotX: { value: STAGE1_UI_ROTATION[0], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotY: { value: STAGE1_UI_ROTATION[1], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotZ: { value: STAGE1_UI_ROTATION[2], min: -Math.PI, max: Math.PI, step: 0.001 },
    }),
  })

  return (
    <StageShell
      stageId="intro"
      panelWidth={400}
      panelHeight={585}
      scale={0.7}
      panel={<IntroPanel isDesktop={isDesktopLayout} />}
      panelPosition={[uiPosX, uiPosY, uiPosZ]}
      panelRotation={[uiRotX, uiRotY, uiRotZ]}
    />
  )
}

// ============================================================================
// INTRO PANEL
// ============================================================================

interface IntroPanelProps {
  isDesktop: boolean
}

function IntroPanel({ isDesktop }: IntroPanelProps) {
  const { state, startConversation, endConversation, loadingProgress, loadModels } = useVoiceState()
  const { isActive: _talkHoverActive, ...talkButtonHover } = useHoverScale(1.05)

  const isConversationActive = state === 'listening' || state === 'thinking' || state === 'speaking'
  const isError = state === 'error'

  const targetProgress = loadingProgress?.progress ?? 0
  const [smoothProgress, setSmoothProgress] = useState(0)
  const smoothRef = useRef(0)
  // Hold at 100% briefly before transitioning — completion celebration
  const completionHoldRef = useRef(0)
  const [showReady, setShowReady] = useState(false)

  const isLoading = (state === 'loading' || state === 'idle') && !showReady

  // Accelerating lerp: slow start (deliberate), fast finish (satisfying)
  // Based on Harrison et al. CHI 2010: accelerating bars perceived 11% shorter
  useFrame((_, delta) => {
    const target = targetProgress
    const prev = smoothRef.current
    const diff = target - prev

    if (Math.abs(diff) < 0.5) {
      if (prev !== target) {
        smoothRef.current = target
        setSmoothProgress(target)
      }
      // Completion hold: when bar reaches 100%, wait 400ms before showing button
      if (state === 'ready' && !showReady && smoothRef.current >= 99) {
        completionHoldRef.current += delta
        if (completionHoldRef.current > 0.4) {
          setShowReady(true)
        }
      }
      return
    }

    // Accelerating speed: 1.5x at 0% → 6x at 100%
    // Early progress feels deliberate, late progress feels snappy
    const t = prev / 100
    const speed = 1.5 + t * 4.5
    smoothRef.current = prev + diff * Math.min(1, delta * speed)
    setSmoothProgress(Math.round(smoothRef.current))
  })

  // Reset completion state when loading starts
  if (state === 'loading' && showReady) {
    setShowReady(false)
    completionHoldRef.current = 0
  }

  const progress = smoothProgress

  return (
    <Panel width={400} height={585} padding={ui.space.xl}>
      <PanelHeader
        title="Welcome"
        badge={!isDesktop ? <Badge variant="accent">ACTIVE</Badge> : undefined}
      />

      <Container flexDirection="column" gap={ui.space.lg} alignItems="center" flexGrow={1}>
        {/* Name & Title */}
        <VStack gap={ui.space.xs} align="center">
          <Text
            fontSize={ui.font.size['2xl']}
            fontWeight={ui.font.weight.bold}
            color={ui.colors.text.primary}
            letterSpacing={-1}
          >
            Sepehr
          </Text>
          <Caption align="center">Software Engineer & AI Enthusiast</Caption>
        </VStack>

        {/* Status Badges */}
        <HStack gap={ui.space.sm} justify="center">
          <Badge variant="success">Open to Work</Badge>
          <Badge variant="muted">Remote</Badge>
        </HStack>

        {/* Voice Interaction */}
        <Container
          flexDirection="column"
          gap={ui.space.sm}
          alignItems="center"
          padding={ui.space.md}
          backgroundColor={ui.colors.bg.surface}
          borderRadius={ui.radius.lg}
          width="100%"
          borderWidth={1}
          borderColor={ui.colors.border.subtle}
        >
          <Body align="center">Click to chat with me!</Body>

          {isLoading ? (
            /* Progress bar button — matches Button size="lg" dimensions */
            <Container
              height={42}
              width="75%"
              borderRadius={6}
              backgroundColor={ui.colors.bg.card}
              borderWidth={1}
              borderColor={ui.colors.border.light}
              overflow="hidden"
              alignItems="center"
              justifyContent="center"
            >
              <Container
                positionType="absolute"
                positionLeft={0}
                positionTop={0}
                positionBottom={0}
                width={`${progress}%`}
                backgroundColor={0xe8e8ec}
                opacity={0.12}
              />
              <Text fontSize={ui.font.size.sm} fontWeight={ui.font.weight.semibold} color={ui.colors.text.primary}>
                {`Loading... ${progress}%`}
              </Text>
            </Container>
          ) : isError ? (
            /* Retry button — matches Button size="lg" dimensions */
            <Container
              height={42}
              paddingX={32}
              borderRadius={6}
              backgroundColor={'#3a1a1a' as any}
              borderWidth={1}
              borderColor={'#dc6464' as any}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={() => loadModels()}
            >
              <Text fontSize={ui.font.size.md} fontWeight={ui.font.weight.semibold} color={'#dc6464' as any}>
                Retry
              </Text>
            </Container>
          ) : (
            /* Ready / Active conversation button */
            <Button
              variant={isConversationActive ? 'destructive' : 'default'}
              size="lg"
              width="75%"
              onClick={() => isConversationActive ? endConversation() : startConversation()}
              {...talkButtonHover}
            >
              <Text fontSize={ui.font.size.md} fontWeight={ui.font.weight.semibold}>
                {isConversationActive ? 'End Conversation' : 'Talk to Me'}
              </Text>
            </Button>
          )}

          {/* Fixed height container to prevent layout shift */}
          <Container height={20} alignItems="center" justifyContent="center">
            <VoiceStatus state={state} />
          </Container>
        </Container>

        {/* Education */}
        <Container flexDirection="column" width="100%">
          <Section title="Education">
            <EducationCard />
            {/* <Badge variant="accent">More</Badge> */}
          </Section>
        </Container>

        {/* Social Links */}
        <Container flexGrow={1} alignItems="center" justifyContent="center">
          <HStack gap={ui.space.lg} justify="center">
            {SOCIAL_LINKS.map((link) => (
              <IconButton key={link.icon} icon={link.icon} href={link.href} />
            ))}
          </HStack>
        </Container>
      </Container>
    </Panel>
  )
}

// ============================================================================
// VOICE STATUS
// ============================================================================

function VoiceStatus({ state }: { state: string }) {
  if (state !== 'listening' && state !== 'thinking' && state !== 'speaking') {
    return null
  }

  const config = {
    listening: { color: ui.colors.success, label: 'Listening...' },
    thinking: { color: ui.colors.secondary, label: 'Thinking...' },
    speaking: { color: ui.colors.primary, label: 'Speaking...' },
  }[state as 'listening' | 'thinking' | 'speaking']

  return (
    <HStack gap={ui.space.sm} align="center">
      <Container
        width={8}
        height={8}
        borderRadius={ui.radius.full}
        backgroundColor={config.color}
      />
      <Text fontSize={ui.font.size.sm} fontWeight={ui.font.weight.normal} color={config.color}>
        {config.label}
      </Text>
    </HStack>
  )
}

// ============================================================================
// EDUCATION CARD
// ============================================================================

function EducationCard() {
  const { isActive, ...hoverScale } = useHoverScale()

  return (
    <Card padding={ui.space.md} width="100%" hover={{
      backgroundColor: ui.colors.bg.card,
      borderColor: ui.colors.primary,
    }} accentColor={isActive ? ui.colors.primary : undefined} {...hoverScale}>
      <VStack gap={ui.space.xs}>
        <Text
          fontSize={ui.font.size.md}
          fontWeight={ui.font.weight.bold}
          color={ui.colors.text.primary}
        >
          {EDUCATION.degree}
        </Text>

        <HStack justify="space-between" width="100%">
          <Text fontSize={ui.font.size.sm} fontWeight={ui.font.weight.normal} color={ui.colors.text.accent}>
            {EDUCATION.school}
          </Text>
          <Caption>{EDUCATION.year}</Caption>
        </HStack>

        <Body>{EDUCATION.focus}</Body>
      </VStack>
    </Card>
  )
}

// ============================================================================
// ICON BUTTON
// ============================================================================

const IconButton = memo(function IconButton({ icon, href }: { icon: keyof typeof ICONS; href: string }) {
  return (
    <Container
      width={48}
      height={48}
      borderRadius={ui.radius.full}
      backgroundColor={ui.colors.bg.surface}
      borderWidth={1}
      borderColor={ui.colors.border.subtle}
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      hover={{
        backgroundColor: ui.colors.bg.card,
        borderColor: ui.colors.primary,
      }}
      onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
    >
      <Svg
        src={ICON_SRCS[icon]}
        width={22}
        height={22}
        color={ui.colors.text.secondary}
      />
    </Container>
  )
})

export default IntroStage
